import CRMPageTemplate from '@components/Common/CRMPageTemplate';
import CRMStatusChip from '@components/Common/CRMStatusChip';
import ErrorMessage from '@components/Common/ErrorMessage';
import FormField from '@components/Common/FormField';
import SelectFormField from '@components/Common/SelectFormField';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import MintlyTable from '@components/Common/Table';
import type { ColumnsConfig } from '@components/Common/Table/cardFields';
import { getLeadsQuery } from '@constants/graphQLQueries';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import MoveToInboxOutlinedIcon from '@mui/icons-material/MoveToInboxOutlined';
import { Box, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { formatDate } from '@helpers/date';
import { humanizeEnum } from '@helpers/labels';
import { useRememberListView } from '@helpers/listView';
import type { Lead } from './lead';

const rowsPerPage = 25;

/**
 * Inbound managed-hiring leads (consultation form + "request premium" submissions).
 * Read-only triage: a row opens the lead's workspace (/leads/:leadId), and a lead with a linked
 * employer opens that employer from there, where an HR sends the contract. Uses the shared list
 * stack (CRMPageTemplate + MintlyTable pagination + reusable form fields) like every other
 * Control Center list page.
 *
 * Search, source and page live in the query string, so Back from a lead returns to the same view.
 */
const Leads: React.FC = () => {
  const navigate = useNavigate();
  useRememberListView('/leads');
  const [searchParams, setSearchParams] = useSearchParams();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const search = searchParams.get('q') ?? '';
  const source = searchParams.get('filter_source') || 'ALL';
  const page = Math.max(parseInt(searchParams.get('page') ?? '0', 10) || 0, 0);

  /** One writer for the three keys. A filter change always goes back to the first page. */
  const setView = (next: { q?: string; source?: string; page?: number }) => {
    const params = new URLSearchParams(searchParams);
    const put = (key: string, value: string) => (value ? params.set(key, value) : params.delete(key));
    if (next.q !== undefined) put('q', next.q);
    if (next.source !== undefined) put('filter_source', next.source === 'ALL' ? '' : next.source);
    const p = next.page ?? (next.q !== undefined || next.source !== undefined ? 0 : page);
    put('page', p > 0 ? String(p) : '');
    setSearchParams(params, { replace: true });
  };

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await fetchGraphQLApiData(getLeadsQuery());
        if (active) setLeads(data?.getLeads ?? []);
      } catch {
        if (active) setError('Failed to load leads. Please try again.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const sources = useMemo(
    () => Array.from(new Set(leads.map((l) => l.source).filter(Boolean))) as string[],
    [leads],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return leads.filter((l) => {
      if (source !== 'ALL' && l.source !== source) return false;
      if (!q) return true;
      return `${l.company ?? ''} ${l.contactPerson ?? ''} ${(l.roles ?? []).join(' ')} ${l.city ?? ''}`
        .toLowerCase()
        .includes(q);
    });
  }, [leads, search, source]);

  const pageRows = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page],
  );

  const sourceOptions = useMemo(
    () => [{ value: 'ALL', label: 'All sources' }, ...sources.map((s) => ({ value: s, label: humanizeEnum(s) }))],
    [sources],
  );

  const selectors = ['company', 'contact', 'location', 'roles', 'source', 'status', 'createdAt'];
  const tableHeadRow: Record<string, React.ReactNode> = {
    company: 'Company',
    contact: 'Contact',
    location: 'City',
    roles: 'Roles',
    source: 'Source',
    status: 'Status',
    createdAt: 'Created',
  };
  const tableBodyRow: Record<string, (row: Lead) => React.ReactNode> = {
    company: (l) => <Typography className="lead-company">{l.company || 'Unknown company'}</Typography>,
    contact: (l) => <>{[l.contactPerson, l.phone, l.workEmail].filter(Boolean).join(' · ') || '-'}</>,
    location: (l) => l.city || '-',
    roles: (l) => {
      const roles = (l.roles ?? []).filter(Boolean).join(', ') || 'No roles listed';
      const extra = [
        l.approxOpenings ? `~${l.approxOpenings} openings` : '',
        l.timeline || '',
      ]
        .filter(Boolean)
        .join(' · ');
      return extra ? `${roles} · ${extra}` : roles;
    },
    // The stored enum (PREMIUM_HIRING_REQUEST) was printed raw and truncated mid-word.
    source: (l) => humanizeEnum(l.source),
    status: (l) => (l.status ? <CRMStatusChip status={l.status} /> : '-'),
    createdAt: (l) => formatDate(l.createdAt),
  };
  /* Status is sixth; the tablet default (the first three) dropped it at 900px. */
  const columns: ColumnsConfig = {
    company: { card: 'title', tablet: true },
    contact: { card: 'subtitle', tablet: true },
    status: { card: 'status', tablet: true },
  };

  /*
   * Filters go in the inline slot, not the staged filter panel: `filtered` is computed from an
   * array already in memory, so filtering is instant. Making the user click Apply to hide two rows
   * would read as the page being slow.
   */
  return (
    <CRMPageTemplate
      title="Premium Hiring leads"
      subtitle="Inbound consultation and Premium Hiring requests."
      icon={<MoveToInboxOutlinedIcon />}
      totalCount={filtered.length}
      inlineFilters={(
        <>
          <Box className="cc-inline-filter--search">
            <FormField
              required={false}
              name="search"
              label="Search"
              size="small"
              value={search}
              placeholder="Search company, contact, role, city"
              onChange={(_name, value) => setView({ q: String(value) })}
            />
          </Box>
          <Box>
            <SelectFormField
              required={false}
              name="source"
              label="Source"
              size="small"
              value={source}
              options={sourceOptions}
              onChange={(_name, value) => setView({ source: String(value) })}
            />
          </Box>
        </>
      )}
    >

      {error ? (
        <ErrorMessage message={error} />
      ) : loading ? (
        <SkeletonLoader columns={selectors.length} />
      ) : (
        <MintlyTable
          rows={pageRows}
          selectors={selectors}
          columns={columns}
          tableHeadRow={tableHeadRow}
          tableBodyRow={tableBodyRow as Record<string, (row: any, index: number) => React.ReactNode>}
          onRowClick={(row: Lead) => navigate(`/leads/${row._id}`)}
          pagination={{
            enabled: true,
            page,
            rowsPerPage,
            totalNumberOfRows: filtered.length,
            handleChangePage: (_e, newPage) => setView({ page: newPage }),
          }}
        />
      )}
    </CRMPageTemplate>
  );
};

export default Leads;
